import { useRef } from 'react'

/**
 * Erisilebilir segmented control - `role="radiogroup"` + `role="radio"`.
 *
 * SADECE sabit, 2-3 secenekli kumeler icin (tema, yogunluk gibi). Buyuyebilen
 * veya 3'u gecen kumeler icin (dil gibi) native `<select>` kullanin - bkz.
 * `i18n/LanguagePicker.tsx` dosya bası yorumu: klavye gezinmesi, ekran
 * okuyucu anonsu ve mobil platform secici NATIVE `<select>`te bedava gelir,
 * segmented control'de hepsi elle yeniden yazilmasi gerekir.
 */

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange(next: T): void
  'aria-label': string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
}: Props<T>) {
  const groupRef = useRef<HTMLDivElement>(null)

  function focusIndex(index: number) {
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons?.[index]?.focus()
  }

  function handleKeyDown(event: React.KeyboardEvent, index: number) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return
    }
    event.preventDefault()
    const last = options.length - 1
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowRight'
            ? (index + 1) % options.length
            : (index - 1 + options.length) % options.length
    onChange(options[next].value)
    focusIndex(next)
  }

  return (
    <div ref={groupRef} role="radiogroup" aria-label={ariaLabel} className="segmented">
      {options.map((option, index) => {
        const checked = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            className={checked ? 'segmented-option segmented-option-on' : 'segmented-option'}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
